import AddIcon from "@mui/icons-material/Add";
import GradingIcon from "@mui/icons-material/Grading";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { SelectArrayInput, useRecordContext, useRefresh } from "react-admin";
import { Control, FormProvider, useFieldArray, useForm, useFormContext, useWatch } from "react-hook-form";
import { useMutation } from "react-query";
import * as backendApi from "../api/backend.api";
import { getAuthenticatedBackendClient } from "../api/backend.api";
import { getAuthenticatedServerClient } from "../api/server.api";
import { reportError } from "../backoffice.utils";
import { InvoiceEndpoint, ManualInvoice, ManualInvoiceProductTypeEnum } from "../generated/backendClient";
import { useDialog } from "../hooks/useDialog";
import { DialogProps } from "../misc/DialogProps";
import { Student } from "../providers/studentsProvider";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { EurosInput } from "../inputs/MoneyInput";
import { useEffect, useState } from "react";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { Column } from "../misc/Column";
import { Row } from "../misc/Row";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { DateTimeInput } from "../inputs/DateTimeInput";
import type { DrivingSchoolServiceType } from "../providers/drivingSchoolServiceTypesProvider";

type FormValues = {
  products: {
    type: ManualInvoiceProductTypeEnum | "";
    selectKey: string; // Unique key for the Select dropdown (enum value or `st:${serviceTypeId}`)
    price: number;
    vatPercentage?: number;
    datetime?: DateTime;
    title?: string; // For EXTERNAL_SERVICE_TYPE: the service type title
    serviceTypeId?: string; // For EXTERNAL_SERVICE_TYPE: the server-managed ServiceType UUID
  }[];
  price: number;
  bookedTrainingIds: string[];
};

type InvoiceMetadata = Awaited<ReturnType<InvoiceEndpoint["invoiceManualInvoiceMetadataRetrieve"]>>;

export function CreateManualInvoiceButton() {
  const { isDialogOpen, openDialog, closeDialog } = useDialog();
  return (
    <>
      <Button startIcon={<AddIcon />} variant="outlined" onClick={openDialog}>
        Manuelle Rechnung
      </Button>
      <CreateManualInvoiceDialogs open={isDialogOpen} onClose={closeDialog} openDialog={openDialog} />
    </>
  );
}

/** Shows the InvoiceFormDialog and a ConfirmDialog afterward. */
function CreateManualInvoiceDialogs(props: { openDialog: () => void } & DialogProps) {
  const { open, onClose, openDialog } = props;
  const DEFAULT_VAT_PERCENTAGE = 19;

  const {
    isDialogOpen: isConfirmDialogOpen,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();
  const form = useForm<FormValues>({
    defaultValues: {
      products: [{ type: "", selectKey: "", vatPercentage: DEFAULT_VAT_PERCENTAGE, datetime: DateTime.now() }],
      bookedTrainingIds: [],
    },
  });

  return (
    <FormProvider {...form}>
      <InvoiceFormDialog open={open} onClose={onClose} onConfirm={openConfirmDialog} />
      <InvoiceConfirmDialog
        open={isConfirmDialogOpen}
        onClose={() => {
          form.reset();
          closeConfirmDialog();
        }}
        onBack={() => {
          closeConfirmDialog();
          openDialog();
        }}
      />
    </FormProvider>
  );
}

function InvoiceFormDialog(props: { onConfirm: () => void } & DialogProps) {
  const { open, onClose, onConfirm } = props;
  const resetAndClose = () => {
    reset();
    onClose();
  };
  const student = useRecordContext<Student>();

  const { trigger, reset } = useFormContext<FormValues>();
  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="lg" fullWidth>
      <DialogTitle>Rechnung erstellen und bezahlen</DialogTitle>
      <DialogCloseButton onClick={resetAndClose} />
      <DialogContent>
        <InvoiceFormTable />
        <Column>
          <Typography variant="body2">Zuweisung Ausbildung</Typography>
          <Row>
            <SelectArrayInput
              variant="standard"
              source="bookedTrainingIds"
              label={false}
              helperText={false}
              sx={{
                "& .MuiInputBase-root": { margin: 0, minWidth: "160px" },
                "& legend": {
                  display: "none",
                },
              }}
              choices={student.bookedTrainings.map((bt) => ({ id: bt.id, name: bt.drivingLicenseClass }))}
            />
          </Row>
        </Column>
      </DialogContent>
      <DialogActions>
        <Button
          endIcon={<NavigateNextIcon />}
          variant="outlined"
          onClick={async () => {
            const valid = await trigger();
            if (!valid) return;
            onClose();
            onConfirm();
          }}
        >
          Weiter
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** A unified dropdown choice for both hardcoded position types and driving-school ServiceTypes. */
type DropdownChoice = {
  key: string; // unique key: enum value for hardcoded types, `st:${id}` for service types
  label: string;
  productType: ManualInvoiceProductTypeEnum;
  vatPercentage: number;
  defaultPrice?: number; // default price in EUR, if available
  title?: string; // set only for EXTERNAL_SERVICE_TYPE entries
  serviceTypeId?: string; // set only for EXTERNAL_SERVICE_TYPE entries
};

function InvoiceFormTable() {
  const { register, control, formState, getValues, setValue } = useFormContext<FormValues>();
  const { fields } = useFieldArray<FormValues>({ name: "products", control });
  const student = useRecordContext<Student>();

  const [invoiceMetadata, setInvoiceMetadata] = useState<InvoiceMetadata | undefined>(undefined);
  const [serviceTypes, setServiceTypes] = useState<DrivingSchoolServiceType[] | undefined>(undefined);

  if (!invoiceMetadata) {
    getAuthenticatedBackendClient()
      .then((client) =>
        client.invoice
          .invoiceManualInvoiceMetadataRetrieve()
          .then(setInvoiceMetadata)
          .catch((error) => reportError("Failed to load invoice metadata", error)),
      )
      .catch((error) => reportError("Failed to get authenticated backend client", error));
  }

  useEffect(() => {
    let isActive = true;
    getAuthenticatedServerClient()
      .then((client) => client.getDrivingSchoolServiceTypes({ params: { id: student.drivingSchoolId } }))
      .then((result) => {
        if (isActive) setServiceTypes(result);
      })
      .catch((error) => {
        reportError("Failed to load service types", error);
        if (isActive) setServiceTypes([]);
      });
    return () => {
      isActive = false;
    };
  }, [student.drivingSchoolId]);

  if (!invoiceMetadata || !serviceTypes) {
    return <LoadingIndicator />;
  }
  const DEFAULT_VAT_PERCENTAGE = 19;

  // Build hardcoded choices from backend metadata
  const hardcodedChoices: DropdownChoice[] = Object.entries(invoiceMetadata.manual_invoice_type_descriptions)
    .map(([key, description]) => ({
      key,
      label: description,
      productType: key as ManualInvoiceProductTypeEnum,
      vatPercentage:
        (invoiceMetadata.manual_invoice_type_vat_percentages as Record<string, number>)[key] ?? DEFAULT_VAT_PERCENTAGE,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Build ServiceType choices
  const serviceTypeChoices: DropdownChoice[] = serviceTypes
    .map((st) => ({
      key: `st:${st.id}`,
      label: st.title,
      productType: "EXTERNAL_SERVICE_TYPE" as ManualInvoiceProductTypeEnum,
      vatPercentage: Number(st.vatRate),
      defaultPrice: st.price !== null ? Number(st.price) : undefined,
      title: st.title,
      serviceTypeId: st.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const choicesByKey = new Map<string, DropdownChoice>(
    [...hardcodedChoices, ...serviceTypeChoices].map((c) => [c.key, c]),
  );

  return (
    <TableContainer>
      <Table width="100%">
        <TableHead>
          <TableRow>
            <TableCell width="100px">Position</TableCell>
            <TableCell width="100px" align="right">
              Datum & Uhrzeit
            </TableCell>
            <TableCell width="250px" align="right">
              Beschreibung*
            </TableCell>
            <TableCell width="100px" align="right">
              Anzahl
            </TableCell>
            <TableCell width="100px" align="right">
              MwSt.
            </TableCell>
            <TableCell width="250px" align="right">
              Brutto*
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((_, index) => {
            return (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell align="right" sx={{ paddingBottom: "24px" }}>
                  <DateTimeInput<FormValues>
                    source={`products.${index}.datetime`}
                    allowPastDates
                    allowFutureDates
                    label=""
                  />
                </TableCell>
                <TableCell align="right" sx={{ paddingBottom: 0 }}>
                  <FormControl sx={{ minWidth: 250 }}>
                    <Select<string>
                      {...register(`products.${index}.selectKey`, { required: true })}
                      defaultValue={""}
                      value={getValues(`products.${index}.selectKey`) || ""}
                      onChange={(e) => {
                        const selectedKey = e.target.value;
                        const choice = choicesByKey.get(selectedKey);
                        if (!choice) return;
                        setValue(`products.${index}.selectKey`, selectedKey, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        setValue(`products.${index}.type`, choice.productType);
                        setValue(`products.${index}.vatPercentage`, choice.vatPercentage);
                        setValue(`products.${index}.title`, choice.title);
                        setValue(`products.${index}.serviceTypeId`, choice.serviceTypeId);
                        setValue(`products.${index}.price`, choice.defaultPrice ?? ("" as unknown as number), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      id="invoice-type"
                      variant="standard"
                      displayEmpty
                      error={!!formState.errors.products?.[index]?.selectKey}
                      renderValue={(value) => {
                        if (!value)
                          return (
                            <Typography
                              component="span"
                              sx={{
                                color: formState.errors.products?.[index]?.selectKey ? "error.main" : "text.primary",
                              }}
                            >
                              Bitte auswählen
                            </Typography>
                          );
                        return choicesByKey.get(value)?.label ?? value;
                      }}
                    >
                      {hardcodedChoices.length > 0 && <ListSubheader>Standardpositionen</ListSubheader>}
                      {hardcodedChoices.map((choice) => (
                        <MenuItem key={choice.key} value={choice.key}>
                          {choice.label}
                        </MenuItem>
                      ))}
                      {serviceTypeChoices.length > 0 && <ListSubheader>Eigene Rechnungstypen</ListSubheader>}
                      {serviceTypeChoices.map((choice) => (
                        <MenuItem key={choice.key} value={choice.key}>
                          {choice.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{" " /* ... for vertical alignment with other inputs */}</FormHelperText>
                  </FormControl>
                </TableCell>
                <TableCell align="right">1</TableCell>
                <TableCell align="right" sx={{ paddingBottom: 0 }}>
                  <TextField
                    {...register(`products.${index}.vatPercentage`, { valueAsNumber: true })}
                    id="invoice-vat-percentage"
                    defaultValue={DEFAULT_VAT_PERCENTAGE}
                    variant="standard"
                    sx={{ "& input": { textAlign: "right" }, "& .MuiInput-root": { minWidth: "100px" } }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    disabled
                    helperText=" " // ... for vertical alignment with other inputs
                  />
                </TableCell>
                <TableCell align="right" sx={{ paddingBottom: 0 }}>
                  <EurosInput
                    label=""
                    source={`products.${index}.price`}
                    validate={(value) => (value ? null : "Bitte Rechnungsbetrag eingeben")}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow sx={{ "& td": { borderBottom: "none" } }}>
            <TableCell colSpan={3} />
            <TableCell sx={{ pb: "5px" }} align="right">
              Netto
            </TableCell>
            <TableCell sx={{ pb: "5px" }} align="right">
              MwSt
            </TableCell>
            <TableCell sx={{ pb: "5px" }} align="right">
              Brutto
            </TableCell>
          </TableRow>

          <Total control={control} />
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Total(props: { control: Control<FormValues> }) {
  const { control } = props;
  const formValues = useWatch({
    name: "products",
    control,
  });
  const total = formValues.reduce((acc, current) => acc + (current.price || 0), 0);
  const vat = formValues.reduce((acc, current) => acc + (current.price * (current.vatPercentage || 0)) / 100, 0) || 0;

  return (
    <TableRow sx={{ "& td": { borderBottom: "none" } }}>
      <TableCell size="small" align="right" colSpan={3}>
        Summe
      </TableCell>
      <TableCell size="small" align="right" sx={{ fontWeight: "bold" }}>
        {formatEuro(total - vat)}
      </TableCell>
      <TableCell size="small" align="right" sx={{ fontWeight: "bold" }}>
        {formatEuro(vat)}
      </TableCell>
      <TableCell size="small" align="right" sx={{ fontWeight: "bold" }}>
        {formatEuro(total)}
      </TableCell>
    </TableRow>
  );
}

function InvoiceConfirmDialog(props: { onBack: () => void } & DialogProps) {
  const { open, onClose, onBack } = props;
  const student = useRecordContext<Student>();
  const form = useFormContext<FormValues>();
  const refresh = useRefresh();

  const total = form.watch("products").reduce((acc, current) => acc + (current.price || 0), 0);

  const { mutate: createManualInvoice, isLoading } = useMutation("b2cInvoices", backendApi.createManualInvoice, {
    onSuccess: () => {
      onClose();
      refresh();
      return showSuccessToast(`Rechnung wird erstellt. Dies kann ein wenig dauern.`);
    },
    onError: (error) => {
      reportError(`Failed to create invoice for student ${student.name} (${student.id})`, error);
      showErrorToast(`Rechnung konnte nicht erstellt werden.`);
    },
  });

  const onSubmit = (values: FormValues) => {
    try {
      // For now, we only support one product per invoice
      // In the future, the invoice type would be inferred from product types or set via form by the user
      if (values.products.some((product) => !product.type)) {
        throw new Error(`Product type is required, but products given as: ${JSON.stringify(values.products)}`);
      }
      const invoice: ManualInvoice = {
        user: { id: student.autovioUserId },
        driving_school_id: student.drivingSchoolId,
        booked_training_ids: values.bookedTrainingIds,
        products: values.products.map((product) => ({
          gross_per_unit: { amount: product.price.toString(), currency: "EUR" },
          quantity: 1,
          type: product.type as ManualInvoiceProductTypeEnum,
          datetime: product.datetime?.toISO(),
          ...(product.type === "EXTERNAL_SERVICE_TYPE"
            ? { title: product.title, vat_percentage: product.vatPercentage, service_type_id: product.serviceTypeId }
            : {}),
        })),
      };
      return createManualInvoice(invoice);
    } catch (error) {
      reportError(`Failed to create invoice for student ${student.name} (${student.id})`, error);
      showErrorToast(`Rechnung konnte nicht erstellt werden.`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Rechnung erstellen und bezahlen</DialogTitle>
      <DialogCloseButton onClick={onClose} disabled={isLoading} />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing="1em">
            <DialogContentText>
              Rechnung mit Summe <b>{formatEuro(total)}</b> erstellen und bezahlen?
            </DialogContentText>
            <DialogContentText>
              Es kann ein wenig dauern, bis die neue Rechnung in der Liste zu sehen ist.
            </DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={onBack} disabled={isLoading}>
            Zurück
          </Button>
          <LoadingButton
            loading={isLoading}
            loadingPosition="start"
            startIcon={<GradingIcon />}
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
          >
            Erstellen und bezahlen
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function formatEuro(value: number) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);
}
